import { ActionIcon, Divider, Stack, Text, TextInput, Title, useMantineTheme } from '@mantine/core'
import { IconX } from '@tabler/icons-react'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { isAddress } from 'viem'

import { BLANK_ADDRESS } from '@/constants/blockchain'
import useL1NS from '@/hooks/useL1NS'
import useLocalStorage from '@/hooks/useLocalStorage'
import { useAuth } from '@/plugins/auth'
import { baseAddressUrl } from '@/utils/table'

import { Creator, UserIdentity } from '../../../utils/utils'
import RootButton from '../../Buttons/RootButton'
import { AtIcon, CheckedIcon, InvalidIcon } from '../../SpecialInputText'
import { ItemCreator } from '../Templates/utils'
import classes from './Credits.module.css'

interface CreditsProps {
  prevStep: () => void
  nextStep: () => void
  saveDraft: () => void
  metadata: any
  creator: DeepReadonly<ItemCreator>
  setCreator: (creator: ItemCreator) => void
}

const Credits: React.FC<CreditsProps> = ({
  prevStep,
  nextStep,
  saveDraft,
  metadata,
  creator,
  setCreator,
}) => {
  const { t } = useTranslation()
  const theme = useMantineTheme()
  const { address } = useAuth()
  const [userIdentity] = useLocalStorage<UserIdentity>(`userIdentity-${address}`)
  const { getL1nsAddress, getL1nsName } = useL1NS()

  // Metadata
  const creatorData = metadata.royalties
  const { min, max } = creatorData.rules

  // Credits
  const [nCreators, setNCreators] = useState<DeepReadonly<Creator[]>>(
    // if creator length is more than 0, use that, otherwise use the default
    creator.credits && creator?.credits?.length > 0
      ? [
          ...creator.credits.map((c, index) => ({
            id: index,
            username: c,
            address: '',
            valid: undefined,
          })),
        ]
      : [{ id: 0, username: '', address: '', valid: undefined }]
  )

  // check if addresses have usernames
  const checkAddresses = useCallback(
    () =>
      Promise.all(
        nCreators.map(async c => {
          if (isAddress(c.username)) {
            const reverseHandle = await getL1nsName(c.username)
            if (reverseHandle) {
              c.username = reverseHandle
            }
          }
          return c
        })
      ),
    [nCreators]
  )

  useEffect(() => {
    checkAddresses().then(updatedCreators => {
      setNCreators(updatedCreators)
    })
  }, [])

  // Check if there are repeated creators
  const repeatedCreators = useMemo(
    () =>
      nCreators.some((c1, index) =>
        nCreators.some(
          (c2, index2) =>
            (c1.address.toLowerCase() === c2.address.toLowerCase() ||
              c1.address.toLowerCase() === c2.username.toLowerCase() ||
              c1.username === c2.username) &&
            index !== index2 &&
            c1.address !== ''
        )
      ),
    [nCreators]
  )

  const repeatedCreatorErrorMessage = useMemo(
    () =>
      repeatedCreators && (
        <Text style={{ color: theme.colors.notification[0], fontSize: '14px' }}>
          <Trans i18nKey="components.studio.royalties.error.duplicatedCreator">
            You cannot add the same creator twice
          </Trans>
        </Text>
      ),
    [repeatedCreators]
  )

  const isYourself = useCallback(
    (name: string) => {
      if (name === address || (userIdentity?.username && name === userIdentity?.username)) {
        return true
      }
      return false
    },
    [address, userIdentity]
  )

  const handleCheckStatusAction = async (c: Creator) => {
    if (!c.username) {
      c.valid = false
      return
    }
    if (!isAddress(c.username)) {
      try {
        const reverseAddress = await getL1nsAddress(c.username)
        if (reverseAddress && reverseAddress !== BLANK_ADDRESS) {
          c.address = reverseAddress
          c.valid = true
        } else {
          c.valid = false
        }
      } catch (error) {
        c.valid = false
        console.error(error)
      }
    } else if (c.username.length === 42) {
      const reverseHandle = await getL1nsName(c.username)
      if (reverseHandle) {
        c.address = c.username
        c.username = reverseHandle
      }
      c.address = c.username
      c.valid = true
    } else {
      c.valid = false
    }
  }

  const handleUsernameChange = async (c: Creator, value: string) => {
    // Reset the valid address flag
    // Update the username
    setNCreators(prevCreators => {
      const updatedCreators = [...prevCreators]
      const index = updatedCreators.findIndex(cr => cr.id === c.id)
      updatedCreators[index].username = value
      updatedCreators[index].valid = undefined

      return updatedCreators
    })
  }
  // Check if all addresses are valid
  const confirmCredits = useCallback(async () => {
    await Promise.all(nCreators.map(c => handleCheckStatusAction(c)))
  }, [nCreators])

  const handleGenerate = useCallback(() => {
    if (nCreators.some(c => c.valid === false) || repeatedCreators) {
      return
    }
    const prevId = nCreators[nCreators.length - 1].id
    if (nCreators.length <= max - min) {
      // get last element of nCreators array
      setNCreators(prevItems => [
        ...prevItems,
        { id: prevId + 1, username: '', address: '', valid: undefined },
      ])
    }
  }, [nCreators, repeatedCreators])

  const handleDelete = useCallback((id: number) => {
    setNCreators(prevItems => prevItems.filter(item => item.id !== id))
  }, [])

  const [isProcessing, setIsProcessing] = useState<boolean>(false)
  const addNewCreator = useCallback(async () => {
    if (isProcessing) {
      return
    }
    setIsProcessing(true)
    // Confirm Credits
    await confirmCredits()
    // Generate new creator slot
    handleGenerate()
    setIsProcessing(false)
  }, [nCreators, isProcessing])

  // function that returns an array of converted addresses in case of username
  const convertUsernamesToAddresses = useCallback(async () => {
    const convertedAddresses = await Promise.all(
      nCreators.map(async c => {
        if (!isAddress(c.username)) {
          try {
            const reverseAddress = await getL1nsAddress(c.username)
            if (reverseAddress && reverseAddress !== BLANK_ADDRESS) {
              return reverseAddress
            }
          } catch (error) {
            console.error(error)
          }
        }
        return c.username
      })
    )
    return convertedAddresses
  }, [nCreators])

  const continueCredits = useCallback(async () => {
    if (nCreators.length === 1 && nCreators[0].username === '') {
      saveDraft()
      nextStep()
    }
    setIsProcessing(true)
    await confirmCredits()
    setIsProcessing(false)

    const hasInvalidCreator =
      nCreators.some(c => c.valid === false) ||
      repeatedCreators ||
      nCreators.some(c => isYourself(c.username))
    if (!hasInvalidCreator) {
      const convertedAddresses = await convertUsernamesToAddresses()
      setCreator({
        name: address || '',
        link: `${baseAddressUrl}${address}`,
        credits: convertedAddresses,
      })
      nextStep()
      saveDraft()
    }
  }, [nCreators, userIdentity, address, repeatedCreators, isProcessing])

  // Define icon left display on username input section
  const iconLeft = useCallback((c: Creator) => {
    if (c.valid === false || isYourself(c.username)) {
      return <InvalidIcon />
    }
    if (c.valid) {
      return <CheckedIcon />
    }
    return <AtIcon />
  }, [])

  const addCreators = nCreators.map((c, index) => (
    <Stack key={c.id} style={{ flexDirection: 'row', position: 'relative' }}>
      <TextInput
        leftSection={iconLeft(c)}
        key={`text-${c.id}`}
        placeholder={t('components.studio.credits.placeholderAddress', 'Username or Address')}
        className={classes.textInput}
        value={c.username}
        error={
          c.valid === false ? (
            <>
              <Trans i18nKey="components.studio.credits.invalidAddressOrUsername">
                <Title order={5} style={{ color: theme.colors.notification[0] }}>
                  Invalid Username
                </Title>
                <Text style={{ color: theme.colors.notification[0], fontSize: '14px' }}>
                  This is not an active address or username on the LAMINA1 Hub. Try again or report
                  the issue{' '}
                  <a
                    href="https://blocksurvey.io/lamina1-hub-feedback-D.KIfzAfR9.0KNKCBAMGGQ-o"
                    target="_blank"
                    rel="noreferrer"
                  >
                    here
                  </a>
                  .
                </Text>
              </Trans>
            </>
          ) : isYourself(c.username) ? (
            t('components.studio.credits.ownAddress', 'You are already a creator')
          ) : (
            ''
          )
        }
        onChange={e => handleUsernameChange(c, e.currentTarget.value)}
      />

      {index !== 0 && (
        <>
          <Divider orientation="vertical" className={classes.divider} />
          <ActionIcon className={classes.deleteButton} onClick={() => handleDelete(c.id)}>
            <IconX size={20} />
          </ActionIcon>
        </>
      )}
    </Stack>
  ))

  return (
    <Stack style={{ textAlign: 'left', gap: '14px' }}>
      <Divider />
      <Stack style={{ flexDirection: 'row', gap: '15px', alignItems: 'center' }}>
        <Title order={3}>{t('components.studio.credits.title', 'Credit Co-Creators')}</Title>
        <Text
          style={{
            color: theme.colors.textAndIcon[0],
            fontSize: '14px',
            fontFamily: 'monument-grotesk-regularitalic',
          }}
        >
          {t('components.studio.optional', 'optional')}
        </Text>
      </Stack>
      <Text style={{ color: theme.colors.paragraph[0], fontSize: '14px' }}>
        <Trans i18nKey="components.studio.credits.description1">
          Input the username or address of any co-creators you’d like to credit. If there are no
          co-creators, click ‘Continue’ to skip this step.
        </Trans>
      </Text>

      {addCreators}
      {repeatedCreatorErrorMessage}
      <Stack style={{ flexDirection: 'row' }}>
        <RootButton
          secondary
          expand
          onClick={addNewCreator}
          disabled={
            nCreators.some(c => c.username === '' || c.valid === false) ||
            repeatedCreators ||
            nCreators.some(c => isYourself(c.username))
          }
        >
          {t('components.studio.credits.addCreator', 'Add Creator')}
        </RootButton>
      </Stack>

      <Stack style={{ flexDirection: 'row', gap: '10px', alignItems: 'center' }}>
        <RootButton secondary onClick={prevStep}>
          {t('buttons.previous')}
        </RootButton>
        <RootButton
          onClick={continueCredits}
          disabled={
            repeatedCreators ||
            nCreators.some(c => isYourself(c.username)) ||
            nCreators.some(c => c.valid === false)
          }
        >
          {t('buttons.continue')}
        </RootButton>
      </Stack>
    </Stack>
  )
}
export default Credits
